import { Button, TextField } from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { useEffect, useState } from "react";
import { CiWarning } from "react-icons/ci";
import { TfiLock } from "react-icons/tfi";
import { CiCreditCard1 } from "react-icons/ci";
import { BsCalendar2Check } from "react-icons/bs";
import { GoPerson } from "react-icons/go";


export default function DefaultForm(props: any) {

    const { 
        payment_method,
        setPaymentStatus,
        enqueueSnackbar,
        ShowLoader,
        is_subscription = false,
        additionalData = {},
    } = props;

    const [formValues, setFormValues] = useState({
        name: "",
        card_number: "",
        card_expiration: "",
        card_cvv: "",
    });
    const [errors, setErrors] = useState({
        card_expiration: false,
    });

    // Detectar el tipo de tarjeta basado en IIN (Issuer Identification Number)
    const detectCardType = (cardNumber: string) => {
        const cleaned = cardNumber.replace(/\D/g, '');
        
        // American Express: 34, 37 (15 dígitos)
        if (/^3[47]/.test(cleaned)) {
            return { type: 'amex', length: 15, format: 'amex' };
        }
        
        // Visa: 4 (13 o 16 dígitos, pero generalmente 16)
        if (/^4/.test(cleaned)) {
            return { type: 'visa', length: 16, format: 'standard' };
        }
        
        // Mastercard: 51-55, 2221-2720 (16 dígitos)
        if (/^5[1-5]/.test(cleaned) || /^2(22[1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720)/.test(cleaned)) {
            return { type: 'mastercard', length: 16, format: 'standard' };
        }
        
        // Discover: 6011, 622126-622925, 644-649, 65 (16 dígitos)
        if (/^6(?:011|5|4[4-9]|22(1(2[6-9]|[3-9]\d)|[2-8]\d{2}|9([01]\d|2[0-5])))/.test(cleaned)) {
            return { type: 'discover', length: 16, format: 'standard' };
        }
        
        // Por defecto, asumimos formato estándar de 16 dígitos
        return { type: 'unknown', length: 16, format: 'standard' };
    };

    const formatCardNumber = (value: string) => {
        const cleaned = value.replace(/\D/g, '');
        const cardInfo = detectCardType(cleaned);
        
        if (cardInfo.format === 'amex') {
            // Formato Amex: 4-6-5 (ej: 3782 822463 10005)
            return cleaned
                .replace(/(\d{4})(\d{0,6})(\d{0,5})/, (match, p1, p2, p3) => {
                    let formatted = p1;
                    if (p2) formatted += ' ' + p2;
                    if (p3) formatted += ' ' + p3;
                    return formatted;
                })
                .substring(0, 17); // 15 dígitos + 2 espacios
        } else {
            // Formato estándar: 4-4-4-4
            return cleaned
                .replace(/(.{4})/g, '$1 ')
                .trim()
                .substring(0, 19); // 16 dígitos + 3 espacios
        }
    };

    const formatExpirationDate = (value: string) => {
        let cleanedValue = value.replace(/\D/g, '');

        if (cleanedValue.length > 2) {
            cleanedValue = cleanedValue.substring(0, 2) + '/' + cleanedValue.substring(2);
        }

        return cleanedValue.substring(0, 5);
    };

    const handleExpirationChange = (e: any) => {
        const formattedValue = formatExpirationDate(e.target.value);
        setFormValues({ ...formValues, card_expiration: formattedValue });

        validateExpirationDate(formattedValue);
    };

    const validateExpirationDate = (value: string) => {
        const [month, year] = value.split('/').map(Number);
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; 
        const currentYear = currentDate.getFullYear() % 100;

        let isValid = true;

        if (month > 12 || month < 1) {
            isValid = false;
        }
        if (year < currentYear || (year === currentYear && month < currentMonth)) {
            isValid = false;
        }

        setErrors({ ...errors, card_expiration: !isValid });
    };


    const submitPayment = async () => {

        if (payment_method != "free") {
            if (!formValues.name || formValues.name.length < 2) {
                enqueueSnackbar('Please enter a valid name', { variant: 'error' });
                return;
            }

            // Validar longitud de tarjeta según el tipo detectado
            const cardDigits = formValues.card_number.replace(/\D/g, '');
            const cardInfo = detectCardType(cardDigits);
            
            if (!formValues.card_number || cardDigits.length !== cardInfo.length) {
                enqueueSnackbar('Please enter a valid card number', { variant: 'error' });
                return;
            }

            if (errors.card_expiration) {
                enqueueSnackbar('Please enter a valid expiration date', { variant: 'error' });
                return;
            }

            if (!formValues.card_cvv || formValues.card_cvv.length < 3) {
                enqueueSnackbar('Please enter a valid CVV', { variant: 'error' });
                return;
            }
        }

        const cart = JSON.parse(localStorage.getItem('cart') || '{}');

        const body: any = {
            cart,
            payment_method: payment_method,
            name: formValues.name,
            card_number: formValues.card_number,
            card_expiration: formValues.card_expiration,
            card_cvv: formValues.card_cvv,
        }

        if (is_subscription) {
            body.additionalData = additionalData;
        }


        ShowLoader(true);
        
        // Create request body based on payment method
        const requestBody: any = {
            cart,
            payment_method: payment_method,
        };
        
        // Only add card details if not free payment
        if (payment_method !== "free") {
            requestBody.name = formValues.name;
            requestBody.card_number = formValues.card_number;
            requestBody.card_expiration = formValues.card_expiration;
            requestBody.card_cvv = formValues.card_cvv;
        }
        
        await ApiRequest({
            url:  is_subscription ? "/payments/subscriptionCheckout" : "/payments/defaultCheckout",
            method: "post",
            body: requestBody,
            setResponse: (response: any) => {
                setPaymentStatus(response);
            }
        });
        ShowLoader(false);
        
    }

    const [cssClassList, setCssClassList] = useState("w-input");
    const [buttonClassList, setButtonClassList] = useState("w-button");

    useEffect(() => {
        const inputElement = document.querySelector('[pc="fname"]');

        if (inputElement) {
            const classList = inputElement.classList.value;
            setCssClassList(classList);
        }

        const buttonElement = document.querySelector('[pc-step="accordion-next"]');
        if (buttonElement) {
            let classList = buttonElement.classList.value;
            classList = classList.replace("is-disabled", "").trim(); // Eliminar "is-disabled"
            setButtonClassList(classList);
        }



    } ,[]);



    return (
        <div className="form_wrapper">

            {payment_method !== "free" && (
                <div className="secure_payment_message">
                    <TfiLock /> 
                    <span>Payments are secure and encrypted</span>
                </div>
            )}
        
            <div className="input_group">
                {payment_method !== "free" && (
                    <>
                        <TextField 
                            placeholder="Name"
                            value={formValues.name}
                            onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                            InputProps={{
                                startAdornment: (
                                    <GoPerson 
                                        size={25} 
                                        style={{ 
                                            marginRight: 10, 
                                            opacity: 0.5
                                        }} 
                                    />
                                ),
                            }}
                            className={cssClassList}
                        />

                        <TextField 
                            placeholder="Card Number"
                            value={formValues.card_number}
                            onChange={(e) => setFormValues({ ...formValues, card_number: formatCardNumber(e.target.value) })}
                            inputProps={{ maxLength: 19 }}
                            InputProps={{
                                startAdornment: (
                                    <CiCreditCard1 
                                        size={25} 
                                        style={{ 
                                            marginRight: 10, 
                                            opacity: 0.5
                                        }} 
                                    />
                                ),
                            }}
                            className={cssClassList}
                        />

                        <div className="input_row">
                            <TextField 
                                placeholder="Expiration Date (MM/YY)"
                                value={formValues.card_expiration}
                                onChange={handleExpirationChange}
                                error={errors.card_expiration}
                                helperText={errors.card_expiration ? "Invalid date MM/YY" : ""}
                                className={cssClassList}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <BsCalendar2Check 
                                            size={20} 
                                            style={{ 
                                                marginRight: 10, 
                                                opacity: 0.5
                                            }} 
                                        />
                                    ),
                                }}
                            />

                            <TextField 
                                placeholder="CVV"
                                value={formValues.card_cvv}
                                onChange={(e) => setFormValues({ ...formValues, card_cvv: e.target.value })}
                                inputProps={{ maxLength: 4 }}
                                className={cssClassList}
                                sx={{ height: "auto" }}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <TfiLock 
                                            size={25} 
                                            style={{ 
                                                marginRight: 10, 
                                                opacity: 0.5
                                            }}
                                        />
                                    ),
                                }}
                            />
                        </div>
                    </>
                )}

                {payment_method === "test_mode" && (
                    <div className="test_mode">
                        <span className="test_mode_warning"><CiWarning />
                        Transaction In Test Mode</span>
                    </div>
                )}

                <div className="payment_button_container">
                    <button 
                        className={buttonClassList}
                        onClick={submitPayment}
                        style={{width: "100%"}}
                    >
                        Place Order
                    </button>
                </div>
            </div>
        </div>
    );
}
